import { useCallback, useEffect, useRef, useState } from 'react'
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  restrictToVerticalAxis,
  restrictToFirstScrollableAncestor,
} from '@dnd-kit/modifiers'
import { validate } from 'uuid'
import { useLocalStorage } from 'usehooks-ts'
import {
  usePrivateProjectNotes,
  useTeamProjectNotes,
} from '../../hooks/useNotesFactory'
import useMoveNote, { MoveNoteOption } from '../../hooks/useMoveNote'
import {
  AcceptInvitationDialog,
  ManageMembersDialog,
} from '../user/TeamspaceDialogs'
import { useSidebarProvider } from '../../contexts/Sidebar'
import { SidebarItem } from './SidebarItem'
import { type SidebarEntry } from './SidebarBuilder'
import { useIsOnline } from '../../providers/NetworkProvider'
import { showSignOut, useAuthenticatedUser } from '../../providers/UserProvider'
import { AuthType } from '../../utils/User'
import classNames from 'classnames'
import { NoteType, SourceDatabase, isTeamspaceNote } from '../../utils/note'
import { findSidebarEntry } from './findSidebarEntry'
import { useIsMobile } from '../../hooks/useIsMobile'
import { createPortal } from 'react-dom'
import { getTeamSpace } from '../../utils/teamSpace'
import CloudLoading from '../../components/CloudLoading'

// Define the data structure for DND Kit items
type SidebarDragData = {
  recordName: string
  noteType: NoteType
  filename: string
  parentRecordName: string | null // This is used in the SidebarItem
  parent?: string | null // This is for TeamSpaceIdentifiable compatibility
  parentPath?: string // For compatibility with existing code
  children?: SidebarEntry[]
  source?: SourceDatabase
}

type MainSidebarProps = {
  showSidebar: boolean
  toggleSidebar: () => void
}

export function MainSidebar({ showSidebar, toggleSidebar }: MainSidebarProps) {
  const { sidebarEntries } = useSidebarProvider()
  const { isFetching: isFetchingPrivate, data: privateProjectNotes } =
    usePrivateProjectNotes()
  const { isFetching: isFetchingTeam, data: teamProjectNotes } =
    useTeamProjectNotes()
  const isOnline = useIsOnline()
  const user = useAuthenticatedUser()

  // #region resizing
  // Manage resizing the sidebar with useLocalStorage
  const sidebarReference = useRef<HTMLDivElement>(null)
  const isResizing = useRef<boolean>(false)
  const [sidebarWidth, setSidebarWidth] = useLocalStorage(
    'sidebarWidth',
    '300px'
  )
  const [isCurrentlyResizing, setIsCurrentlyResizing] = useState(false)

  // Apply the saved width from localStorage when component mounts or width changes
  useEffect(() => {
    if (sidebarReference.current && !isResizing.current) {
      sidebarReference.current.style.width = sidebarWidth
    }
  }, [sidebarWidth])

  const handleMouseDown = (event: React.MouseEvent) => {
    event.preventDefault()
    isResizing.current = true
    setIsCurrentlyResizing(true)
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
  }

  const handleMouseMove = useCallback((event: globalThis.MouseEvent) => {
    if (!isResizing.current) return

    event.preventDefault()

    const newWidth = Math.max(
      150,
      Math.min(
        1000,
        event.clientX -
          (sidebarReference.current?.getBoundingClientRect().left ?? 0)
      )
    )

    if (sidebarReference.current) {
      sidebarReference.current.style.width = `${newWidth.toString()}px`
    }
  }, [])

  const handleMouseUp = useCallback(
    (event: globalThis.MouseEvent) => {
      event.preventDefault()
      isResizing.current = false
      setIsCurrentlyResizing(false)

      if (sidebarReference.current) {
        setSidebarWidth(sidebarReference.current.style.width)
      }

      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    },
    [handleMouseMove, setSidebarWidth]
  )

  // Clean up event listeners when component unmounts
  useEffect(() => {
    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [handleMouseMove, handleMouseUp])
  // #endregion

  // #region drag and drop
  const [draggedId, setDraggedId] = useState<string | undefined>(undefined)
  const moveNote = useMoveNote()
  const isMobile = useIsMobile()

  // Detect if device has touch capability (includes tablets/iPads)
  const isTouchDevice =
    typeof window !== 'undefined' &&
    ('ontouchstart' in window || navigator.maxTouchPoints > 0)

  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: {
      distance: 5,
    },
  })

  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: {
      delay: 300, // Add a delay before drag starts on touch
      tolerance: 8,
    },
  })

  const sensors = useSensors(isTouchDevice ? touchSensor : pointerSensor)

  const handleDragStart = (event: DragStartEvent) => {
    setDraggedId(event.active.id.toString())
  }

  const handleDragEnd = (event: DragEndEvent) => {
    setDraggedId(undefined)

    if (privateProjectNotes === undefined) {
      return
    }

    if (
      (user.authType === AuthType.SUPABASE ||
        user.authType === AuthType.CLOUDKIT_SUPABASE) &&
      teamProjectNotes === undefined
    ) {
      return
    }

    const { active, over } = event
    // if the parent id is not an uuid then set it to undefined
    const parentRecordName =
      over?.id && validate(over.id.toString()) ? over.id.toString() : undefined

    // Get the typed data from the drag event
    const activeData = active.data.current as SidebarDragData | undefined
    const overData = over?.data.current as SidebarDragData | undefined

    // Check if we're actually moving to a different location
    const isDroppingOnSelf = active.id === over?.id
    const isSameParent = activeData?.parentRecordName === parentRecordName
    const isPathMatch =
      activeData?.parentPath &&
      overData?.filename &&
      activeData.parentPath === overData.filename

    // Special case: when moving from special folders to root
    // Items in @Templates, @Archive, @Trash have parentRecordName as undefined
    // but they're actually inside those folders (check parentPath)
    const isMovingFromSpecialFolderToRoot =
      activeData?.parentPath &&
      ['@Templates', '@Archive', '@Trash'].includes(activeData.parentPath) &&
      over?.id === 'notes' &&
      !parentRecordName

    if (
      isDroppingOnSelf ||
      (isSameParent && !isMovingFromSpecialFolderToRoot) ||
      isPathMatch
    ) {
      return
    }

    // Prevent moving between different sources (CloudKit/Supabase)
    // When dropping on root (notes header), check if it's the right source
    if (overData && activeData?.source !== overData?.source) {
      alert('You cannot move a teamspace into a private folder and vice versa.')
      return
    }

    // Prevent moving special folders themselves (@Templates, @Archive, @Trash)
    const noteToMove =
      privateProjectNotes.map.get(active.id.toString()) ??
      teamProjectNotes?.map.get(active.id.toString())
    const specialFolders = ['@Templates', '@Archive', '@Trash']
    if (noteToMove && specialFolders.includes(noteToMove.filename)) {
      alert('Cannot move special folders (@Templates, @Archive, @Trash).')
      return
    }

    // Prevent moving between private notes and team spaces (even when both are in Supabase)
    if (
      activeData?.noteType !== undefined &&
      overData?.noteType !== undefined &&
      isTeamspaceNote(activeData.noteType) !==
        isTeamspaceNote(overData.noteType)
    ) {
      alert(
        'You cannot move a teamspace note into a private folder and vice versa.'
      )
      return
    }

    // Handle the edge case of moving a team space note to the root of private notes
    // or a private note to the root of team spaces (when parentRecordName is undefined/null)
    if (
      activeData?.noteType !== undefined &&
      !parentRecordName && // parentRecordName is undefined when dropping to root
      isTeamspaceNote(activeData.noteType) // If it's a team space note being moved to root, block it
    ) {
      alert('You cannot move a teamspace note into private notes root.')
      return
    }

    // Block moving notes between different team spaces
    if (
      teamProjectNotes &&
      activeData?.noteType &&
      overData?.noteType &&
      over &&
      isTeamspaceNote(activeData.noteType) &&
      isTeamspaceNote(overData.noteType)
    ) {
      // Prepare the source and target data for team space lookup
      const sourceData = {
        recordName: active.id.toString(),
        noteType: activeData.noteType,
        parent: activeData.parentRecordName,
      }

      const targetData = {
        recordName: over.id.toString(),
        noteType: overData.noteType,
        parent: overData.parentRecordName,
      }

      // Get the team spaces for both notes directly using our data
      const sourceTeamSpace = getTeamSpace(teamProjectNotes.map, sourceData)
      const targetTeamSpace = getTeamSpace(teamProjectNotes.map, targetData)

      // If both notes have different team spaces, prevent the move
      if (
        sourceTeamSpace &&
        targetTeamSpace &&
        sourceTeamSpace.recordName !== targetTeamSpace.recordName
      ) {
        alert('You cannot move notes between different team spaces.')
        return
      }
    }

    // If activeData is undefined, we can't proceed with the move
    if (!activeData) {
      return
    }

    // When dropping on the root (notes header), overData might be minimal
    // but we can still proceed with parent as undefined

    const parent = parentRecordName
      ? {
          recordName: parentRecordName,
          noteType: overData?.noteType ?? NoteType.PROJECT_NOTE,
        }
      : undefined

    // If we are dragging a note to a different parent, then move it
    moveNote.mutate({
      recordName: active.id.toString(),
      noteType: activeData.noteType,
      parent,
      children: activeData.children ?? [],
      privateProjectNotes: privateProjectNotes.map,
      teamProjectNotes: teamProjectNotes?.map,
    } as MoveNoteOption)
  }
  // #endregion

  // #region manage teamspace
  const [manageTeamspaceId, setManageTeamspaceId] = useState<
    | {
        id: string
        title: string
      }
    | undefined
  >(undefined)
  const handleSetManageTeamspaceId = useCallback(
    (id: string, title: string) => {
      setManageTeamspaceId({ id, title })
    },
    []
  )

  const closeManageTeamspace = useCallback(() => {
    setManageTeamspaceId(undefined)
  }, [])
  // #endregion

  return (
    <div
      className={classNames('relative z-10 h-dvh', {
        'md:w-0': !showSidebar,
      })}
    >
      <div
        className={classNames(
          'fixed inset-0 bg-black opacity-40',
          showSidebar ? 'block md:hidden' : 'hidden'
        )}
        onClick={toggleSidebar}
      />
      <button
        onClick={toggleSidebar}
        className='hidden bg-transparent px-1 py-4 opacity-25 transition hover:bg-gray-200 hover:opacity-100 md:absolute md:-right-8 md:top-1/2 md:block md:-translate-y-1/2 dark:hover:bg-gray-700'
      >
        <i
          className={`fa-solid fa-chevron-${showSidebar ? 'left' : 'right'}`}
        />
      </button>
      <div
        className={classNames(
          'absolute right-0 top-0 z-10 h-full w-[6px] cursor-ew-resize',
          'transition-colors duration-150',
          isCurrentlyResizing
            ? 'bg-blue-400 dark:bg-blue-600'
            : 'hover:bg-slate-200 hover:dark:bg-slate-400'
        )}
        onMouseDown={handleMouseDown}
      />
      <div
        ref={sidebarReference}
        className={classNames(
          'flex h-dvh grow flex-col items-center gap-y-5 overflow-y-auto overflow-x-hidden border-r-2 bg-stone-100 px-0 pt-4 max-md:min-w-[90vw] dark:bg-[#333333]',
          'absolute md:relative', // Make it absolute on mobile and ensure it's above content
          'shadow-lg md:shadow-none',
          showSidebar ? 'md:w-full' : 'md:w-0',
          showSidebar ? 'translate-x-0' : '-translate-x-full',
          { 'opacity-10': !isOnline },
          { 'transition-all duration-200 ease-in-out': !isCurrentlyResizing }
        )}
      >
        <div className='-my-2 flex w-full items-center justify-between md:hidden'>
          <div className='flex items-center pl-3'>
            <button
              data-tooltip-id='my-tooltip'
              data-tooltip-content='Logout'
              type='button'
              className='flex flex-none items-center justify-center p-1.5 hover:bg-gray-500/25'
              onClick={() => {
                showSignOut(user)
              }}
            >
              <i className='fas fa-user'></i>
            </button>
            <CloudLoading />
          </div>
          <button
            onClick={toggleSidebar}
            className='hover:bg-gray-200 dark:hover:bg-gray-700'
          >
            <i className={`fa-solid fa-chevron-left`} />
          </button>
        </div>
        <div
          className={classNames(
            'loading-spinner-sm absolute hidden md:right-1 md:top-1',
            isFetchingPrivate || isFetchingTeam ? 'md:inline' : ''
          )}
        />

        <DndContext
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          sensors={sensors}
          modifiers={[
            restrictToVerticalAxis,
            // Removed restrictToFirstScrollableAncestor to fix auto-scroll offset issues
          ]}
          autoScroll={{
            // Configure auto-scroll behavior to fix offset issues
            threshold: {
              // Start auto-scrolling when within 50px of edges
              x: 0.1, // Disable horizontal auto-scroll
              y: 0.15, // 15% of container height
            },
            // Scroll speed
            interval: 5,
            // Acceleration when near edges
            acceleration: 10,
          }}
        >
          <nav className='mr-2 flex w-full flex-1 flex-col pb-32'>
            <ul
              role='list'
              className='flex flex-1 select-none flex-col gap-y-7'
            >
              {sidebarEntries.map((navItem: SidebarEntry) => (
                <SidebarItem
                  key={navItem.recordName}
                  item={navItem}
                  onManageTeamspace={handleSetManageTeamspaceId}
                  toggleSidebar={toggleSidebar}
                  isDragging={Boolean(draggedId)}
                />
              ))}
            </ul>
            {createPortal(
              <DragOverlay
                // The optimistic update of tanstack query is too late
                // So the overlay will always return to its original position instead of the new one
                // eslint-disable-next-line unicorn/no-null
                dropAnimation={null}
                modifiers={[restrictToVerticalAxis]}
              >
                {draggedId ? (
                  <ul
                    role='list'
                    className='flex flex-1 select-none flex-col gap-y-7 opacity-50'
                  >
                    {(() => {
                      const { entry } = findSidebarEntry(
                        sidebarEntries,
                        draggedId
                      )
                      return (
                        entry && (
                          <SidebarItem
                            item={entry}
                            onManageTeamspace={handleSetManageTeamspaceId}
                            collapsed
                            isDragOverlay={true}
                            toggleSidebar={toggleSidebar}
                          />
                        )
                      )
                    })()}
                  </ul>
                ) : undefined}
              </DragOverlay>,
              document.body
            )}
          </nav>
        </DndContext>
      </div>
      {isOnline ? undefined : (
        <div className='absolute left-0 top-0 h-full w-full' />
      )}
      <AcceptInvitationDialog />
      <ManageMembersDialog
        teamspaceID={manageTeamspaceId}
        closeManageTeamspace={closeManageTeamspace}
      />
    </div>
  )
}
